// INC2-CONTACT in the in-memory API: the contact list (services/api/src/routes/contacts.ts) and
// a connection's grants (routes/outbox.ts `connectionGrants.*`). The fake company has no contacts
// and no grants (no sample data): tests add them with `addContact` and through the grant routes.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, page, peekJson, problem } from "./fake";

export type FakeContact = OperationOutput<"contacts.list">["data"][number];
type Grant = OperationOutput<"connectionGrants.create">;
type AccessMatrix = OperationOutput<"access.matrix">;
type Connection = OperationOutput<"connections.list">["data"][number];
type DeSummary = OperationOutput<"digitalEmployees.list">["data"][number];

export const FAKE_CONTACT_ROUTES = [
  "contacts.list",
  "contacts.get",
  "connectionGrants.listAll",
  "connectionGrants.list",
  "connectionGrants.create",
  "connectionGrants.revoke",
  "access.matrix",
  "access.apply",
  "access.template",
] as const satisfies readonly RouteId[];

export function createContactsFake(deps: {
  now: () => Date;
  newId: () => string;
  /** Another twin's handler (the access matrix reads the connections and DEs it serves). */
  call?: (route: RouteId, ctx: Ctx) => Response | Promise<Response> | undefined;
}) {
  const contacts: FakeContact[] = [];
  const grants: Grant[] = [];

  const handlers: Partial<Record<RouteId, Handler>> = {
    "contacts.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const q = (url.searchParams.get("q") ?? "").trim().toLowerCase();
      const archived = url.searchParams.get("archived") === "true";
      const rows = contacts
        .filter(
          (c) =>
            c.company_id === principal.company.id &&
            c.merged_into_id === null &&
            (c.archived_at !== null) === archived &&
            (!q ||
              [c.display_name, c.email, c.phone, c.organisation_name].some((v) =>
                (v ?? "").toLowerCase().includes(q),
              )),
        )
        .sort((a, b) => (a.id < b.id ? 1 : -1));
      return json(page(rows, url));
    },
    "contacts.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const c = contacts.find((x) => x.company_id === principal.company.id && x.id === params.id);
      if (!c) return notFound();
      const { email: _e, phone: _p, organisation_name: _o, ...row } = c;
      return json({ ...row, identities: [], consent: [], organisation: null, duplicate_ids: [] });
    },
    "connectionGrants.listAll": (ctx) => {
      const { principal, url } = ctx;
      if (!principal) return notFound();
      const connection = url.searchParams.get("connection");
      const holder = url.searchParams.get("holder");
      const data = grants.filter(
        (g) =>
          g.company_id === principal.company.id &&
          g.revoked_at === null &&
          (!connection || g.connection_id === connection) &&
          (!holder || (g.grantee_kind === "membership" && g.grantee_id === holder)),
      );
      // As the API: the connections the grants are on, named for the caller (who manages each
      // in the fake), so a DE's Connections section needs no connections.list of its own.
      const used = new Set(data.map((g) => g.connection_id));
      const connections = connectionsOf(ctx)
        .filter((c) => used.has(c.id))
        .map((c) => ({
          id: c.id,
          label: c.label,
          provider: c.provider,
          identity_display: c.identity_display ?? null,
          state: c.state,
        }));
      return json({ data, connections });
    },
    "connectionGrants.list": ({ principal, params }) => {
      if (!principal) return notFound();
      return json({
        data: grants.filter(
          (g) =>
            g.company_id === principal.company.id &&
            g.connection_id === params.id &&
            g.revoked_at === null,
        ),
      });
    },
    "connectionGrants.create": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const b = body as Pick<Grant, "grantee_kind" | "grantee_id" | "level"> & {
        families?: Grant["families"];
        limits?: Grant["limits"];
      };
      const at = deps.now().toISOString();
      for (const g of grants)
        if (
          g.company_id === principal.company.id &&
          g.connection_id === params.id &&
          g.grantee_kind === b.grantee_kind &&
          g.grantee_id === b.grantee_id &&
          g.revoked_at === null
        )
          g.revoked_at = at;
      const membership = principal.me.memberships.find(
        (m) => m.company_id === principal.company.id,
      );
      const g: Grant = {
        company_id: principal.company.id,
        id: deps.newId(),
        connection_id: params.id ?? "",
        grantee_kind: b.grantee_kind,
        grantee_id: b.grantee_id,
        level: b.level,
        families: b.families ?? [],
        limits: b.limits ?? {},
        granted_by_membership_id: membership?.membership_id ?? "",
        revoked_at: null,
        actor_kind: "person",
        actor_membership_id: membership?.membership_id ?? null,
        created_at: at,
        updated_at: at,
      };
      grants.push(g);
      return json(g, 201);
    },
    "connectionGrants.revoke": ({ principal, params }) => {
      if (!principal) return notFound();
      const g = grants.find(
        (x) =>
          x.company_id === principal.company.id &&
          x.connection_id === params.id &&
          x.id === params.grant_id,
      );
      if (!g) return notFound();
      if (g.revoked_at === null) {
        g.revoked_at = deps.now().toISOString();
        g.updated_at = g.revoked_at;
      }
      return json(g);
    },
    // INC2-ACCESS: the matrix over the twin's own connections, DEs and grants (the server's rules,
    // simplified: a DE the caller may intervene on stands for one they supervise).
    "access.matrix": (ctx) => (ctx.principal ? json(matrixOf(ctx)) : notFound()),
    "access.apply": (ctx) => {
      const { principal } = ctx;
      if (!principal) return notFound();
      const b = ctx.body as {
        changes: {
          connection_id: string;
          membership_id: string;
          level: string;
          families?: Grant["families"];
          limits?: Grant["limits"];
        }[];
        act_mode?: string;
      };
      if (b.act_mode === "direct")
        return problem(422, "act_mode_unavailable", "Acting directly is not available yet");
      const m = matrixOf(ctx);
      for (const ch of b.changes) {
        const col = m.columns.find((c) => c.connection_id === ch.connection_id);
        const row = m.rows.find((r) => r.membership_id === ch.membership_id);
        if (!col?.editable) return notFound();
        if (!row || row.kind !== "de") return problem(422, "grant_not_allowed", "Not a DE");
        if (col.scope === "person" && (!row.supervised || ch.level === "manage"))
          return problem(
            422,
            "grant_not_allowed",
            "Your own connection goes only to DEs you supervise",
          );
        if (col.scope === "person" && ch.level === "use_act" && !ch.families?.length)
          return problem(422, "grant_not_allowed", "Name the families");
      }
      const membership = principal.me.memberships.find(
        (x) => x.company_id === principal.company.id,
      );
      const by = membership?.membership_id ?? "";
      let changed = 0;
      for (const ch of b.changes) {
        const at = deps.now().toISOString();
        for (const g of grants)
          if (
            g.company_id === principal.company.id &&
            g.connection_id === ch.connection_id &&
            g.grantee_kind === "membership" &&
            g.grantee_id === ch.membership_id &&
            g.revoked_at === null
          ) {
            g.revoked_at = at;
            changed++;
          }
        if (ch.level !== "none") {
          grants.push({
            company_id: principal.company.id,
            id: deps.newId(),
            connection_id: ch.connection_id,
            grantee_kind: "membership",
            grantee_id: ch.membership_id,
            level: ch.level as Grant["level"],
            families: ch.families ?? [],
            limits: ch.limits ?? {},
            granted_by_membership_id: by,
            revoked_at: null,
            actor_kind: "person",
            actor_membership_id: by,
            created_at: at,
            updated_at: at,
          });
          changed++;
        }
      }
      return json({ changed, matrix: matrixOf(ctx) });
    },
    "access.template": ({ principal, params }) =>
      principal
        ? json({ membership_id: params.id ?? "", template_key: null, proposals: [] })
        : notFound(),
  };

  /** The company's connections, from the twin that serves them. */
  function connectionsOf(ctx: Ctx): Connection[] {
    const co = (ctx.principal as NonNullable<Ctx["principal"]>).company.id;
    const res = deps.call?.("connections.list", {
      ...ctx,
      url: new URL("?limit=200", ctx.url),
      params: { co },
    });
    return res instanceof Response ? (peekJson<{ data: Connection[] }>(res)?.data ?? []) : [];
  }

  /** The matrix as the caller sees it, from the twin's connections and DEs. */
  function matrixOf(ctx: Ctx): AccessMatrix {
    const p = ctx.principal as NonNullable<Ctx["principal"]>;
    const co = p.company.id;
    const ask = <T>(route: RouteId, path: string): T[] => {
      const res = deps.call?.(route, { ...ctx, url: new URL(path, ctx.url), params: { co } });
      return res instanceof Response ? (peekJson<{ data: T[] }>(res)?.data ?? []) : [];
    };
    const conns = ask<Connection>("connections.list", "?limit=200").filter(
      (c) => c.state !== "disconnected",
    );
    const des = ask<DeSummary>("digitalEmployees.list", "?limit=200");
    const columns = conns
      .filter((c) => c.scope === "person" || p.manager)
      .map((c) => ({
        connection_id: c.id,
        label: c.label,
        provider: c.provider,
        scope: c.scope as "company" | "person",
        identity_display: c.identity_display ?? null,
        state: c.state,
        editable: true,
      }));
    const rows = des.map((d) => ({
      membership_id: d.membership_id,
      name: d.display_name,
      kind: "de" as const,
      seat_id: d.seat?.id ?? null,
      seat_title: d.seat?.title ?? null,
      team_ids: [] as string[],
      supervised: d.can_intervene === true,
      act_mode: "approval" as const,
    }));
    const visible = new Set(columns.map((c) => c.connection_id));
    const cells = grants
      .filter(
        (g) =>
          g.company_id === co &&
          g.revoked_at === null &&
          g.grantee_kind === "membership" &&
          visible.has(g.connection_id) &&
          rows.some((r) => r.membership_id === g.grantee_id),
      )
      .map((g) => ({
        connection_id: g.connection_id,
        membership_id: g.grantee_id,
        level: g.level,
        via: "direct" as const,
        grant_id: g.id,
        families: g.families,
        limits: g.limits,
        changed_by_membership_id: g.granted_by_membership_id,
        changed_at: g.created_at,
        inherited_level: null,
        sources: [
          { via: "direct" as const, level: g.level, families: g.families, limits: g.limits },
        ],
      }));
    return {
      rows,
      columns,
      cells,
      teams: [],
      direct_available: false,
      can_set_act_mode: false,
    };
  }

  return {
    handlers,
    /** Tests: put a contact in a company. */
    addContact: (c: FakeContact) => contacts.push(c),
  };
}
